import React from 'react';

import { ProjectContext } from '../project-state';
import type { ProjectSnapshot } from '../project-state-adapter';
import { setProjectSnapshot } from './project-state-adapter.internal';

type Props = {
  /** Override adapter status when the provider does not reflect loading/error. */
  statusOverride?: ProjectSnapshot['status'];
};

const EMPTY_SNAPSHOT: ProjectSnapshot = {
  status: 'idle',
  projectId: null,
  project: null,
  source: 'unknown',
} as const;

/**
 * Renders nothing. Subscribes to ProjectContext and pushes a read-only snapshot
 * into projectStateAdapter so non-React code (viewer, asset-manager, etc.) can
 * read current project without touching React. Must be mounted inside
 * ProjectContext.Provider.
 *
 * Sync is done in useEffect (not during render) to keep render pure and avoid issues
 * with StrictMode, Concurrent Mode, and SSR. Consumers (useProjectSnapshot() and
 * non-React code) may see the previous snapshot for one commit before the effect runs.
 */
export function ProjectStateAdapterBridge(props: Props) {
  const ctx = React.useContext(ProjectContext);
  const project = ctx?.project ?? null;
  const status = props.statusOverride ?? (project ? 'ready' : 'loading');
  const sharedFolderId = ctx?.sharedFolderId;

  React.useEffect(() => {
    const snap: ProjectSnapshot = {
      status,
      projectId: project?.id ?? null,
      project,
      sharedFolderId: ctx?.sharedFolderId,
      isAdmin: ctx?.isAdmin?.(),
      isCompanyAdmin: ctx?.isCompanyAdmin?.(),
      isOrganizationAdmin: ctx?.isOrganizationAdmin?.(),
      source: ctx?.sharedFolderId ? 'shared-view' : project ? 'project' : 'unknown',
    };
    setProjectSnapshot(snap);
    return () => setProjectSnapshot(EMPTY_SNAPSHOT);
  }, [status, project, sharedFolderId, ctx]);

  return null;
}
